
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, googleProvider, isConfigured } from '../services/firebaseConfig';

// Fix: Namespace import for auth to bypass resolution issues
import * as firebaseAuth from 'firebase/auth';
const authModule = firebaseAuth as any;
const onAuthStateChanged = authModule.onAuthStateChanged;
const signInWithPopup = authModule.signInWithPopup;
const signOut = authModule.signOut;
const deleteUser = authModule.deleteUser;
const signInAnonymously = authModule.signInAnonymously;

// Define User type as any to avoid 'no exported member' error
type User = any;

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signInWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    loginAsGuest: () => Promise<void>;
    deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    signInWithGoogle: async () => { },
    logout: async () => { },
    loginAsGuest: async () => { },
    deleteAccount: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const deletingRef = React.useRef(false);

    useEffect(() => {
        let mounted = true;

        // If Firebase is configured, listen to auth state changes
        if (isConfigured && auth) {
            const unsubscribe = onAuthStateChanged(auth, (currentUser: any) => {
                if (deletingRef.current) return;

                if (mounted) {
                    setUser(currentUser);
                    setLoading(false);
                }
            });
            return () => {
                mounted = false;
                unsubscribe();
            };
        }

        // Fallback: No Firebase -> Not logged in.
        if (mounted) {
            setLoading(false);
        }

        return () => { mounted = false; };
    }, []);

    const loginAsGuest = async () => {
        if (isConfigured && auth) {
            try {
                setLoading(true);
                // Real Anonymous Auth (Generates valid UID + Token for Cloud Functions)
                await signInAnonymously(auth);
                // State update handled by onAuthStateChanged listener
            } catch (error) {
                console.error("Anonymous login failed", error);
                setLoading(false);
                throw error;
            }
        } else {
            console.warn("Firebase not configured. Cannot sign in anonymously.");
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        if (isConfigured && auth && googleProvider) {
            try {
                await signInWithPopup(auth, googleProvider);
                // State update handled by onAuthStateChanged listener
            } catch (error: any) {
                console.error("Error signing in with Google", error);
                throw error;
            }
        }
    };

    const logout = async () => {
        try {
            if (isConfigured && auth) {
                await signOut(auth);
            }
        } catch (error) {
            console.warn("Firebase signout non-fatal error", error);
        } finally {
            setUser(null);
            window.location.reload(); // Hard reload to clear any deep state
        }
    };

    const deleteAccount = async () => {
        if (user) {
            try {
                deletingRef.current = true; // Block auth state updates
                if (isConfigured && auth) {
                    await deleteUser(user);
                }
                // Flag for login page to show success message
                localStorage.setItem('ikigai_account_deleted', 'true');
                // Do NOT setUser(null) here, rely on reload
                window.location.reload();
            } catch (error) {
                console.error("Error deleting account", error);
                deletingRef.current = false; // Reset on error
                throw error;
            }
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, signInWithGoogle, logout, loginAsGuest, deleteAccount }}>
            {children}
        </AuthContext.Provider>
    );
};
